import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, Check, X } from "lucide-react";
import { api, API } from "../../lib/api";
import ExportButton from "./ExportButton";
import { exportPath } from "../../lib/exportFile";
import Pagination from "./Pagination";

const PAGE_SIZE = 25;

const SOURCE_LABELS = { lead: "Lead", preinscriere: "Preînscriere", webinar: "Webinar" };
const SOURCE_COLORS = {
  lead: "bg-[#EAF0FB] text-[#185FA5]",
  preinscriere: "bg-[#F0E7F4] text-[#7A4986]",
  webinar: "bg-[#EAF4EE] text-[#1F7A52]",
};

// Marketer sync status → label + color.
function syncBadge(r) {
  if (r.marketer_unsubscribed) return { label: "Dezabonat", cls: "bg-[#FBEAE5] text-[#B4541F]" };
  const s = r.marketer_sync_status;
  if (s === "ok") return { label: "Sincronizat", cls: "bg-[#E7F4EE] text-[#1F7A52]" };
  if (s === "failed") return { label: "Eșuat", cls: "bg-[#FBEAE5] text-[#B4541F]" };
  return { label: "În așteptare", cls: "bg-[#F1EFE8] text-[#5F5E5A]" };
}

const inp =
  "bg-white border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm text-[#373A45] focus:ring-2 focus:ring-[#965EA7] focus:border-transparent outline-none";

export default function MarketingConsentsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [consent, setConsent] = useState("all");
  const [source, setSource] = useState("all");
  const [sync, setSync] = useState("all");
  const [q, setQ] = useState("");

  const buildQuery = useCallback((pageArg) => {
    const p = new URLSearchParams();
    p.set("consent", consent);
    p.set("source", source);
    p.set("sync", sync);
    if (q.trim()) p.set("q", q.trim());
    p.set("page", String(pageArg));
    p.set("page_size", String(PAGE_SIZE));
    return p;
  }, [consent, source, sync, q]);

  const load = useCallback((pageArg) => {
    setLoading(true);
    api.get(`/admin/marketing-consents?${buildQuery(pageArg).toString()}`)
      .then((r) => {
        setItems(r.data?.items || []);
        setTotal(r.data?.total || 0);
        setPage(r.data?.page || pageArg);
      })
      .catch(() => toast.error("Nu am putut încărca consimțămintele."))
      .finally(() => setLoading(false));
  }, [buildQuery]);

  // Reload on filter change (reset to page 1).
  useEffect(() => { load(1); }, [consent, source, sync]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (e) => { e.preventDefault(); load(1); };

  const toggleConsent = useCallback(async (r) => {
    const next = !r.consent_marketing;
    if (!next && !window.confirm(`Dezabonezi ${r.email} de la newslettere? Modificarea se trimite și în The Marketer.`)) {
      return;
    }
    try {
      await api.patch(`/admin/marketing-consents/${r.source}/${r.id}`, { consent_marketing: next });
      setItems((prev) => prev.map((x) =>
        (x.source === r.source && x.id === r.id)
          ? { ...x, consent_marketing: next, marketer_unsubscribed: !next, marketer_sync_status: undefined }
          : x,
      ));
      toast.success(next ? "Reabonat. Se sincronizează cu The Marketer." : "Dezabonat. Se sincronizează cu The Marketer.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut schimba consimțământul.");
    }
  }, []);

  const handleExport = (format) => {
    const p = buildQuery(1);
    p.delete("page"); p.delete("page_size");
    window.open(`${API}${exportPath("/admin/marketing-consents/export", p, format)}`, "_blank");
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="marketing-consents-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#373A45]">Consimțăminte marketing</h2>
          <p className="text-sm text-[#6B7280] mt-0.5">
            Acordul de marketing pentru toate contactele (leaduri, preînscrieri, webinarii) + starea de sincronizare cu The Marketer.
          </p>
        </div>
        <ExportButton
          onExport={handleExport}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#373A45] hover:bg-[#1f2128] text-white text-xs font-semibold px-3 py-2"
          testid="marketing-consents-export"
        />
      </div>

      <div className="flex items-end gap-3 flex-wrap mb-4">
        <label className="flex flex-col gap-1 text-xs font-semibold text-[#6B7280]">
          Consimțământ
          <select value={consent} onChange={(e) => setConsent(e.target.value)} className={inp} data-testid="filter-consent">
            <option value="all">Toate</option>
            <option value="yes">Da</option>
            <option value="no">Nu</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-semibold text-[#6B7280]">
          Sursă
          <select value={source} onChange={(e) => setSource(e.target.value)} className={inp} data-testid="filter-source">
            <option value="all">Toate</option>
            <option value="lead">Lead</option>
            <option value="preinscriere">Preînscriere</option>
            <option value="webinar">Webinar</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-semibold text-[#6B7280]">
          The Marketer
          <select value={sync} onChange={(e) => setSync(e.target.value)} className={inp} data-testid="filter-sync">
            <option value="all">Toate</option>
            <option value="ok">Sincronizat</option>
            <option value="pending">În așteptare</option>
            <option value="failed">Eșuat</option>
            <option value="unsubscribed">Dezabonat</option>
          </select>
        </label>
        <form onSubmit={onSearch} className="flex items-end gap-2">
          <label className="flex flex-col gap-1 text-xs font-semibold text-[#6B7280]">
            Caută
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="email sau nume" className={inp} data-testid="filter-search" />
          </label>
          <button type="submit" className="inline-flex items-center gap-1 rounded-lg bg-[#965EA7] hover:bg-[#7A4986] text-white text-sm font-semibold px-3 py-2">
            <Search size={14} />
          </button>
        </form>
      </div>

      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#6B7280]"><Loader2 className="inline animate-spin" size={18} /></div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-[#6B7280] text-sm">Niciun rezultat pentru filtrele alese.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E5E7EB]">
                <th className="px-4 py-3">Nume</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Sursă</th>
                <th className="px-4 py-3">Consimțământ</th>
                <th className="px-4 py-3">The Marketer</th>
                <th className="px-4 py-3 text-right">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const sb = syncBadge(r);
                return (
                  <tr key={`${r.source}-${r.id}`} className="border-b border-[#F1EFE8] last:border-0" data-testid={`consent-row-${r.id}`}>
                    <td className="px-4 py-3 font-semibold text-[#373A45]">{r.name || "—"}</td>
                    <td className="px-4 py-3 text-[#6B7280]">{r.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${SOURCE_COLORS[r.source] || ""}`}>
                        {SOURCE_LABELS[r.source] || r.source}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {r.consent_marketing ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#E7F4EE] px-2 py-0.5 text-[10px] font-semibold text-[#1F7A52]">
                          <Check size={11} /> Da
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#F1EFE8] px-2 py-0.5 text-[10px] font-semibold text-[#5F5E5A]">
                          <X size={11} /> Nu
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${sb.cls}`}
                        title={r.marketer_synced_at ? `Sincronizat: ${new Date(r.marketer_synced_at).toLocaleString("ro-RO")}` : ""}
                      >
                        {sb.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => toggleConsent(r)}
                        data-testid={`consent-toggle-${r.id}`}
                        className={`rounded-full px-3 py-1 text-[11px] font-semibold border transition-colors ${
                          r.consent_marketing
                            ? "border-[#E5C8C0] text-[#B4541F] hover:bg-[#FBEAE5]"
                            : "border-[#C0DDC8] text-[#1F7A52] hover:bg-[#E7F4EE]"
                        }`}
                      >
                        {r.consent_marketing ? "Dezabonează" : "Reabonează"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4">
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          totalPages={totalPages}
          hasNext={page < totalPages}
          hasPrev={page > 1}
          onPageChange={(p) => load(p)}
        />
      </div>
    </div>
  );
}
